import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import TwitterIcon from '@mui/icons-material/Twitter';
import ekstraklasaImage from '../assets/images/ekstraklasa.webp';
import liga1Image from '../assets/images/1liga.webp';
import liga2Image from '../assets/images/2liga.webp';
import lowerLeaguesImage from '../assets/images/niskie.webp';
import SupportBox from './SupportBox';
import { useCompetitions } from './CompetitionContext';
import { fetchArticles, formatArticleDate } from '../services/strapiBlog';
import { fetchTeams } from '../services/api';
import { slugify } from '../utils/slugify';

const quickAccessTiles = [
  { title: 'Ekstraklasa', description: 'Symulacje Ekstraklasy!', link: '/liga/432/ekstraklasa', image: ekstraklasaImage },
  { title: '1. Liga', description: 'Symulacje 1. ligi!', link: '/liga/433/1-liga', image: liga1Image },
  { title: '2. Liga', description: 'Symulacje 2. ligi', link: '/liga/434/2-liga', image: liga2Image },
  { title: 'Niższe ligi', description: 'Wybierz z górnego menu (Rozgrywki)', image: lowerLeaguesImage },
];

const defaultArticleImage = '/images/symuliga.webp';

const normalizeSearchText = (value = '') =>
  value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'l')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const hasCompetitionGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value.toLowerCase() !== 'null' && value !== 'Brak';
};

const getCompetitionInfo = (team, competitionsById) => {
  const competitionFromContext = team.competitionId ? competitionsById.get(team.competitionId) : null;
  return {
    id: team.competitionId,
    name: team.competitionName || competitionFromContext?.name || '',
    group: team.competitionGroup || competitionFromContext?.group || '',
  };
};

const getCompetitionLabel = (team, competitionsById) => {
  const competition = getCompetitionInfo(team, competitionsById);
  if (!competition.name) return 'Brak aktywnej ligi';
  return hasCompetitionGroup(competition.group)
    ? `${competition.name} (${competition.group.trim()})`
    : competition.name;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { competitions } = useCompetitions();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamSearchValue, setTeamSearchValue] = useState('');
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState(null);

  const competitionsById = useMemo(() => {
    const entries = Object.values(competitions || {}).flat();
    return new Map(entries.map((competition) => [competition.competition_id, competition]));
  }, [competitions]);

  useEffect(() => {
    let active = true;

    setTeamsLoading(true);
    setTeamsError(null);

    fetchTeams({ countryId: 40 })
      .then((result) => {
        if (!active) return;
        setTeams(
          (result || [])
            .filter((team) => team.teamId && team.teamName)
            .map((team) => ({
              ...team,
              normalizedName: normalizeSearchText(team.teamName),
            }))
            .sort((a, b) => a.teamName.localeCompare(b.teamName, 'pl'))
        );
      })
      .catch((requestError) => {
        if (!active) return;
        console.error('Teams request failed:', requestError);
        setTeamsError('Nie udało się pobrać listy drużyn.');
      })
      .finally(() => {
        if (active) setTeamsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const teamSearchOptions = useMemo(() => {
    const query = normalizeSearchText(teamSearchValue).trim();
    if (query.length < 3) return [];
    const queryParts = query.split(' ').filter(Boolean);

    return teams
      .filter((team) => queryParts.every((part) => team.normalizedName.includes(part)))
      .sort((a, b) => {
        const aStarts = a.normalizedName.startsWith(query) ? 0 : 1;
        const bStarts = b.normalizedName.startsWith(query) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return (b.eloRanking || 0) - (a.eloRanking || 0);
      });
  }, [teamSearchValue, teams]);

  const getTeamUrl = (team) => `/team/${team.teamId}/${slugify(team.teamName)}`;
  const getCompetitionUrl = (team) => {
    const competition = getCompetitionInfo(team, competitionsById);
    return competition.id && competition.name
      ? `/liga/${competition.id}/${slugify(getCompetitionLabel(team, competitionsById))}`
      : null;
  };

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError(null);

    fetchArticles({ page: 1, pageSize: 3 })
      .then((result) => {
        if (!active) return;
        setArticles(result.articles);
      })
      .catch((requestError) => {
        if (!active) return;
        console.error('Strapi latest articles request failed:', requestError);
        setError('Nie udało się pobrać artykułów.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const teamSearchSection = (
    <Box sx={{ px: { xs: '20px', md: '40px' }, pb: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 760,
          mx: 'auto',
          p: { xs: 1.75, sm: 2.25 },
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.045)',
        }}
      >
        <Stack spacing={1.1}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <SportsSoccerIcon sx={{ color: '#FFD700', fontSize: 22 }} />
            <Typography variant="h5" component="h2" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Znajdź drużynę
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Wpisz nazwę klubu i przejdź prosto do jego profilu, bez szukania ligi i grupy w menu.
          </Typography>
          <Autocomplete
            freeSolo
            autoHighlight
            disablePortal
            loading={teamsLoading}
            options={teamSearchOptions}
            inputValue={teamSearchValue}
            onInputChange={(event, value) => setTeamSearchValue(value)}
            onChange={(event, selectedTeam) => {
              if (selectedTeam && typeof selectedTeam !== 'string') {
                navigate(getTeamUrl(selectedTeam));
              }
            }}
            filterOptions={(options) => options}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.teamName)}
            slotProps={{
              popper: {
                placement: 'bottom-start',
                modifiers: [{ name: 'flip', enabled: false }],
              },
              paper: {
                sx: {
                  mt: 0.5,
                  '& .MuiAutocomplete-listbox': {
                    py: 0.5,
                    maxHeight: 330,
                  },
                },
              },
            }}
            noOptionsText={
              teamSearchValue.trim().length < 3
                ? 'Wpisz co najmniej 3 znaki'
                : 'Brak pasujących drużyn'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="np. Hutnik, Polonia, Orzeł..."
                error={Boolean(teamsError)}
                helperText={teamsError || ' '}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => {
              const competitionUrl = getCompetitionUrl(option);

              return (
                <Box
                  component="li"
                  {...props}
                  key={option.teamId}
                  sx={{ p: '0 !important' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      width: '100%',
                      px: 2,
                      py: 0.65,
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.2 }}>
                      {option.teamName}
                    </Typography>
                    {competitionUrl ? (
                      <Typography
                        component={Link}
                        to={competitionUrl}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          navigate(competitionUrl);
                        }}
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          flexShrink: 0,
                          maxWidth: { xs: 140, sm: 260 },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'right',
                          textDecoration: 'none',
                          '&:hover': { color: '#FFD700', textDecoration: 'underline' },
                        }}
                      >
                        {getCompetitionLabel(option, competitionsById)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {getCompetitionLabel(option, competitionsById)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
          />
        </Stack>
      </Paper>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ textAlign: 'center', padding: { xs: '20px', md: '40px' } }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          SymuLiga.pl - centrum predykcji polskiej piłki!
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: '650px', margin: '0 auto' }}>
          Symulujemy wyniki wszystkich lig w Polsce. Sprawdź gdzie skończy Twój klub!
          Chcesz współpracować lub masz pytania? Pisz na kontakt@symuliga.pl.
        </Typography>
        <Button
          component="a"
          href="https://x.com/symuligaPL"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<TwitterIcon sx={{ fontSize: 16 }} />}
          size="small"
          variant="text"
          sx={{ color: '#1da1f2', fontSize: '0.875rem', mb: 0 }}
        >
          Odwiedź nas na X
        </Button>
      </Box>

      <Box sx={{ padding: { xs: '20px', md: '40px' } }}>
        <Grid container spacing={3} justifyContent="center">
          {quickAccessTiles.map((tile) => (
            <Grid item xs={12} sm={6} md={4} key={tile.title}>
              {tile.link ? (
                <Link to={tile.link} style={{ textDecoration: 'none' }}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: tile.featured ? '2px solid #FFD700' : 'none',
                      transform: tile.featured ? 'scale(1.03)' : 'none',
                      boxShadow: tile.featured ? 6 : 1,
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={tile.image}
                      alt={tile.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography variant="h6">{tile.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tile.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Link>
              ) : (
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={tile.image}
                    alt={tile.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="h6">{tile.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tile.description}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Grid>
          ))}
        </Grid>
      </Box>

      {teamSearchSection}

      <SupportBox />

      <Box sx={{ padding: { xs: '20px', md: '40px' } }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Najnowsze artykuły
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress color="warning" />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : articles.length === 0 ? (
          <Alert severity="info">Brak artykułów.</Alert>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            {articles.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: '#2c2c2c',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                  }}
                >
                  <CardActionArea component={Link} to={`/blog/${article.slug}`} sx={{ height: '100%' }}>
                    <CardMedia
                      component="img"
                      height="178"
                      image={article.cover?.url || defaultArticleImage}
                      alt={article.cover?.alternativeText || article.title}
                      sx={{ objectFit: 'cover', backgroundColor: '#1a1a1a' }}
                    />
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 210 }}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        {article.category?.name && <Chip label={article.category.name} size="small" />}

                        {article.publishedAt && (
                          <Chip label={formatArticleDate(article.publishedAt)} size="small" variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="h6" sx={{ lineHeight: 1.25, mb: 1 }}>
                        {article.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', mb: 2, flexGrow: 1 }}>
                        {article.description}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ alignSelf: 'flex-start', color: '#FFD700' }}
                      >
                        Czytaj
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default HomePage;
